import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { useQueries } from '@tanstack/react-query';
import { defaultHttpClient } from '../../../../api/httpClient';
import { queryKey } from '../../../../constant';
import type { AnyObject } from '../../../../types';
import type { AssembleFilterItem, CachedTabData } from './types';

interface TabResponse {
  item_list?: Array<{ item: AnyObject; logging: AnyObject; render?: AnyObject }>;
}

/**
 * 탭 1개의 상품 데이터 fetch
 */
const fetchTab = async (item: AssembleFilterItem): Promise<CachedTabData> => {
  const response = await defaultHttpClient.request<TabResponse>({
    method: 'GET',
    url: `/api/v2/${item.url}`,
  });

  const itemList = response.data?.item_list;
  if (!itemList || itemList.length === 0) {
    throw new Error(`Empty response for ${item.query_value}`);
  }

  return {
    items: itemList.map(i => ({
      item: i.item,
      logging: {
        analytics: i.logging?.analytics ?? {},
        ably_only_log: i.logging?.ably_only_log ?? {},
      },
      render: i.render,
    })),
  };
};

/**
 * ASSEMBLE N개 URL 병렬 fetch + 캐시
 * iOS MetaFeedAssembleFilterGoodsListReactor.fetchAllTabs() 대응
 *
 * - useQueries로 탭별 독립 쿼리 → 자동 캐시/병렬 처리
 * - 찜 동기화: fetch 응답의 like.is_liked를 likeMap에 반영
 *   (iOS fetchItemList .do(onNext:) LikeGoodsDatabase.saveFromRecords 대응)
 */
export const useAssembleFetch = (
  filterItems: AssembleFilterItem[],
  setLikeMap?: Dispatch<SetStateAction<Record<string, boolean>>>,
) => {
  const { cachedTabs, validQueryValues, isLoading } = useQueries({
    queries: filterItems.map(item => ({
      queryKey: queryKey.assembleTab(item.query_value, item.url),
      queryFn: () => fetchTab(item),
      staleTime: Infinity,
      retry: false,
    })),
    combine: results => {
      const allSettled = results.every(q => !q.isPending);
      if (!allSettled) {
        return {
          cachedTabs: new Map<string, CachedTabData>(),
          validQueryValues: new Set<string>(),
          isLoading: true,
        };
      }

      const newCache = new Map<string, CachedTabData>();
      const newValid = new Set<string>();

      results.forEach((q, i) => {
        if (q.isSuccess && q.data) {
          const qv = filterItems[i].query_value;
          newCache.set(qv, q.data);
          newValid.add(qv);
        }
        // 실패한 탭은 필터에서 제외 (iOS 동작과 동일)
      });

      return {
        cachedTabs: newCache,
        validQueryValues: newValid,
        isLoading: false,
      };
    },
  });

  // 찜 동기화 — combine 밖에서 useEffect로 처리
  // isLoading true→false 전환 시점에 1회 실행 (iOS fetchAllTabs 완료 시점 대응)
  // cachedTabs는 combine에서 매번 새 Map으로 반환되므로 deps에 넣으면 무한 루프 발생
  useEffect(() => {
    if (isLoading || !setLikeMap) return;

    const likeUpdates: Record<string, boolean> = {};
    for (const [, tabData] of cachedTabs) {
      for (const entry of tabData.items) {
        const sno = entry.item?.sno;
        const isLiked = entry.item?.like?.is_liked;
        if (sno != null && isLiked != null) {
          likeUpdates[sno] = isLiked;
        }
      }
    }

    if (Object.keys(likeUpdates).length === 0) return;

    // 실제 값이 바뀐 sno가 있을 때만 새 객체 반환 → 불필요한 리렌더 방지
    setLikeMap(prev => {
      let changed = false;
      const next = { ...prev };
      for (const [sno, isLiked] of Object.entries(likeUpdates)) {
        if (next[sno] !== isLiked) {
          next[sno] = isLiked;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, setLikeMap]);

  return { cachedTabs, validQueryValues, isLoading };
};
